import React, { useEffect, useMemo, useRef, useState } from 'react'
import styles from './VerticalTable.module.scss'
import cx from 'classnames'
import { ColumnsPicker, Table, ITableProps, TableProvider, ITableColumnsType } from 'uikit/tables/default'
import { Empty } from 'antd'

interface IVerticalTable<T = Record<string, unknown>> extends ITableProps<T> {
  pageSize: number
  total?: number
  columns: ITableColumnsType<T>
  containerRef?: React.RefObject<HTMLDivElement>
  additionalRefs?: React.RefObject<HTMLDivElement>[]
  disabledColumns?: string[] | null
  dataIsLoading?: boolean
  enableColumnPicker?: boolean
  topBarContainerClassName?: string
  diffHeight?: number
  fetchNextPage?: () => void
  onSelectionChange?: (ids: string[]) => void
  removeFromPickerColumns?: string[]
  tableId?: string
}

const VerticalTable = <T extends Record<string, unknown> = Record<string, unknown>>({
  pageSize,
  columns,
  dataSource,
  children,
  containerRef,
  additionalRefs,
  dataIsLoading = false,
  total,
  disabledColumns = [],
  enableColumnPicker,
  topBarContainerClassName,
  diffHeight,
  fetchNextPage,
  onSelectionChange,
  removeFromPickerColumns = [],
  tableId,
  ...rest
}: IVerticalTable<T>) => {
  const tableTopBarContainerRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef<boolean>(false)

  const [tableHeight, setTableHeight] = useState(0)
  const [page, setPage] = useState<number>(1)

  useEffect(() => {
    if (!rest.loading) {
      isFetchingRef.current = false
    }
  }, [rest.loading])

  useEffect(() => {
    const calculateHeight = () => {
      if (!containerRef?.current || !tableTopBarContainerRef.current || !tableRef.current || !additionalRefs) return

      const containerHeight = containerRef.current.clientHeight
      const tableTopBarContainerHeight = tableTopBarContainerRef.current.clientHeight

      const additionalHeight = additionalRefs.reduce((sum, ref) => {
        return sum + (ref.current ? ref.current.clientHeight : 0)
      }, 0)

      const tableHeader = tableRef.current.querySelector('.ant-table-thead') as HTMLTableSectionElement
      const tableHeaderHeight = tableHeader ? tableHeader.clientHeight : 0

      const newHeight =
        containerHeight - additionalHeight - tableTopBarContainerHeight - tableHeaderHeight - (diffHeight || 0)
      setTableHeight(newHeight > 0 ? newHeight : 0)
    }

    const observer = new ResizeObserver(calculateHeight)
    if (containerRef?.current) observer.observe(containerRef.current)
    additionalRefs?.forEach(ref => ref.current && observer.observe(ref.current))
    if (tableTopBarContainerRef.current) observer.observe(tableTopBarContainerRef.current)
    if (tableRef.current) observer.observe(tableRef.current)

    calculateHeight()

    return () => observer.disconnect()
  }, [containerRef, additionalRefs, diffHeight])

  const isEmptyTable = useMemo(() => (dataSource && dataSource.length === 0) || !dataSource, [dataSource])

  const filteredColumns = useMemo(() => {
    const columnsWithKeys = columns?.map((col, i) => ({ ...col, key: col.key ?? i }))
    if (!disabledColumns || disabledColumns.length === 0) {
      return columnsWithKeys
    }
    return columnsWithKeys.filter(col => !disabledColumns.includes(col.key as string))
  }, [columns, disabledColumns])

  const enablePreferences = Boolean(enableColumnPicker && tableId)

  // disable the on click event for the checkbox column to avoid navigating to another page when trying to select checkboxes
  const onRow = useMemo(() => {
    if (!rest.onRow) return

    return (record: T, index?: number) => {
      const originalOnRow = rest.onRow?.(record, index)

      return {
        ...originalOnRow,
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          // Check if click originated from checkbox column
          const target = event.target as HTMLElement
          const isCheckboxColumn = target.closest('.ant-table-selection-column')

          if (!isCheckboxColumn && originalOnRow?.onClick) {
            originalOnRow.onClick(event)
          }
        },
      }
    }
  }, [rest.onRow])

  return (
    <TableProvider
      columns={filteredColumns}
      disabledPickerColumns={removeFromPickerColumns}
      preferences={{ enabled: enablePreferences, uniqueStorageKey: tableId as string }}
    >
      {(children || enableColumnPicker) && (
        <div ref={tableTopBarContainerRef} className={cx(styles.topBarContainer, topBarContainerClassName)}>
          {children}
          {enableColumnPicker && <ColumnsPicker />}
        </div>
      )}
      <div ref={tableRef}>
        <Table
          {...rest}
          onRow={onRow}
          rowKey='id'
          rootClassName={cx(styles.rootClassName, isEmptyTable && styles.emptyTableRootClassName)}
          scroll={rest.scroll || { x: 'max-content', y: containerRef && additionalRefs ? tableHeight : 'auto' }}
          dataSource={isEmptyTable ? ([{ id: 'empty' }] as any) : dataSource}
          pagination={false}
          onScroll={(e: React.UIEvent<HTMLDivElement>) => {
            const target = e.currentTarget as HTMLElement

            if (
              target.scrollTop + target.clientHeight + 10 >= target.scrollHeight &&
              !dataIsLoading &&
              !isFetchingRef.current &&
              total &&
              page * pageSize < total
            ) {
              isFetchingRef.current = true
              setPage(prevPage => {
                fetchNextPage?.()
                return prevPage + 1
              })
            }
          }}
          rowSelection={
            onSelectionChange
              ? {
                  type: 'checkbox',
                  hideSelectAll: true,
                  onChange: (selectedRowKeys: React.Key[]) => {
                    onSelectionChange(selectedRowKeys as string[])
                  },
                }
              : undefined
          }
          footer={
            !isEmptyTable
              ? rest.footer
              : () => {
                  return (
                    <div className={styles.emptyTable}>
                      <Empty description='No data' image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                  )
                }
          }
        />
      </div>
    </TableProvider>
  )
}

export default VerticalTable
